import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Sector
} from 'recharts';
import { getModelDisplayName } from '../../constants/models';

// Color palette for different models
const MODEL_COLORS = [
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#3B82F6', // Blue
  '#8B5CF6', // Violet
  '#EC4899', // Pink
  '#F97316', // Orange
  '#14B8A6', // Teal
  '#6366F1', // Indigo
];

/**
 * CostChart - Displays cost breakdown by model as a donut chart
 * Shows which models are consuming the most budget
 */
const CostChart = ({ runs, loading }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  // Transform runs data for cost by model chart
  const { chartData, totalCost } = useMemo(() => {
    if (!runs || runs.length === 0) {
      return { chartData: [], totalCost: 0 };
    }
    
    // Group costs by model
    const modelCosts = {};
    runs.forEach(run => {
      const model = run.model || 'Unknown';
      const cost = run.estimated_cost || 0;
      
      if (!modelCosts[model]) {
        modelCosts[model] = {
          model,
          name: getModelDisplayName(model),
          cost: 0,
          runs: 0
        };
      }
      
      modelCosts[model].cost += cost;
      modelCosts[model].runs += 1;
    });
    
    // Convert to array and sort by cost (descending)
    const data = Object.values(modelCosts)
      .sort((a, b) => b.cost - a.cost)
      .map((item, index) => ({
        ...item,
        color: MODEL_COLORS[index % MODEL_COLORS.length]
      }));
    
    const total = data.reduce((sum, m) => sum + m.cost, 0);
    
    // Calculate percentages
    data.forEach(item => {
      item.percentage = total > 0 ? ((item.cost / total) * 100) : 0;
    });
    
    return { 
      chartData: data, 
      totalCost: total
    };
  }, [runs]);

  // Format cost for display
  const formatCost = (value) => {
    if (value >= 1) return `$${value.toFixed(2)}`;
    if (value >= 0.001) return `$${value.toFixed(3)}`;
    if (value > 0) return `$${value.toFixed(4)}`;
    return '$0.00';
  };

  // Active sector render (on hover) - glow effect like Success Rate
  // eslint-disable-next-line react/prop-types
  const renderActiveShape = ({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }) => {
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 4}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          style={{ filter: `drop-shadow(0 0 8px ${fill}80)` }}
        />
      </g>
    );
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  if (loading && (!runs || runs.length === 0)) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-32 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] rounded animate-pulse bg-primary-500/5"></div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Cost by Model
        </h3>
        <div className="h-[200px] flex items-center justify-center">
          <p className="text-sm text-text-tertiary">No cost data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-amber-500/25">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Cost by Model
          </h3>
          <p className="text-xs mt-0.5 text-text-tertiary">
            {chartData.length} model{chartData.length !== 1 ? 's' : ''} used
          </p>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold font-mono text-amber-500">
            {formatCost(totalCost)}
          </span>
          <p className="text-xs text-text-tertiary">
            total spend
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        {/* Donut Chart */}
        <div className="w-[180px] h-[180px] flex-shrink-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                dataKey="cost"
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onMouseEnter={onPieEnter}
                onMouseLeave={onPieLeave}
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color}
                    className="cursor-pointer"
                    style={{ 
                      filter: activeIndex === index ? `drop-shadow(0 0 8px ${entry.color}80)` : 'none'
                    }}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {/* Center text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <span className="text-xs font-mono font-bold text-text-primary">
                {chartData.length}
              </span>
              <p className="text-[9px] text-text-tertiary">models</p>
            </div>
          </div>
        </div>

        {/* Legend - Show max 5, rest scrollable */}
        <div className="flex-1 space-y-2 max-h-[140px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary-500/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500">
          {chartData.map((item, index) => (
            <div 
              key={item.model} 
              className="flex items-center justify-between cursor-pointer"
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ 
                    background: item.color,
                    boxShadow: activeIndex === index ? `0 0 6px ${item.color}` : 'none'
                  }}
                />
                <span 
                  className={`text-xs truncate max-w-[110px] ${activeIndex === index ? 'text-text-primary' : 'text-text-secondary'}`}
                  title={item.model}
                >
                  {item.name}
                </span>
              </div>
              <span className="text-xs font-mono" style={{ color: item.color }}>
                {formatCost(item.cost)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

CostChart.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.shape({
    llm_config: PropTypes.object,
    estimated_cost: PropTypes.number
  })),
  loading: PropTypes.bool
};

export default CostChart;
